import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Mail, Search, Paperclip, Calendar, User, Plus,
  AlertTriangle, ChevronDown, ChevronRight, Download, Clock, 
  FileText, Brain, RefreshCw, Loader2, Upload, X, Star, StarOff,
  MessageSquare, File, Image
} from 'lucide-react';
import EmailDropZone from './EmailDropZone';
import EmailDetailModal from './EmailDetailModal';

const IMPACT_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200',
  none: 'bg-slate-100 text-slate-500 border-slate-200',
};

const SOURCE_COLORS = {
  'email': 'bg-blue-100 text-blue-700 border-blue-200',
  'manual': 'bg-purple-100 text-purple-700 border-purple-200',
  'import': 'bg-cyan-100 text-cyan-700 border-cyan-200',
};

const UnifiedCommunicationLog = ({ 
  projectId, 
  moduleType = null, 
  moduleEntityId = null,
  title = "Communication Log"
}) => {
  // Combined data from both sources
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [filterNeedsAction, setFilterNeedsAction] = useState('all');
  
  // Email detail modal
  const [selectedEmail, setSelectedEmail] = useState(null);
  
  // Manual entry dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({
    from_email: '',
    from_name: '',
    to_emails: '',
    subject: '',
    body_plain: '',
    sent_at: new Date().toISOString().split('T')[0],
    source: 'manual',
    tags: []
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Expanded threads
  const [expandedThreads, setExpandedThreads] = useState({});
  
  // AI analysis
  const [analyzingIds, setAnalyzingIds] = useState(new Set());
  const [threadAnalysis, setThreadAnalysis] = useState(null);
  const [analyzingThread, setAnalyzingThread] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    emails: 0,
    manual: 0,
    needs_action: 0,
    high_risk: 0
  });

  const loadCommunications = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load from both sources
      const [emailsRes, manualRes] = await Promise.all([
        // Imported emails from email_records
        api.get('/correspondence/emails', { 
          params: { 
            project_id: projectId,
            module_type: moduleType,
            module_entity_id: moduleEntityId,
            ...(searchTerm && { search: searchTerm }),
            ...(filterNeedsAction !== 'all' && { needs_action: filterNeedsAction === 'yes' })
          } 
        }).catch(() => ({ data: [] })),
        // Manual correspondence from correspondence_messages
        api.correspondence.getByEvent(moduleEntityId, {
          view: 'chronological',
          ...(searchTerm && { keyword: searchTerm })
        }).catch(() => ({ data: { messages: [] } }))
      ]);

      // Normalize and merge data
      const emails = (emailsRes.data || []).map(e => ({
        ...e,
        _source: 'email',
        _sortDate: new Date(e.sent_at || e.received_at || e.created_at)
      }));

      const manual = (manualRes.data?.messages || []).map(m => ({
        ...m,
        _source: 'manual',
        _sortDate: new Date(m.sent_at || m.created_at)
      }));

      // Combine and sort by date (newest first)
      let combined = [...emails, ...manual];
      
      // Apply source filter
      if (sourceFilter !== 'all') {
        combined = combined.filter(c => c._source === sourceFilter);
      }
      
      combined.sort((a, b) => b._sortDate - a._sortDate);
      
      setCommunications(combined);
      
      // Calculate stats
      setStats({
        total: combined.length,
        emails: emails.length,
        manual: manual.length,
        needs_action: combined.filter(c => c.needs_action).length,
        high_risk: combined.filter(c => (c.ai_impact?.delay_risk_score || 0) >= 70).length
      });
      
    } catch (error) {
      console.error('Failed to load communications:', error);
      toast.error('Failed to load communications');
    } finally {
      setLoading(false);
    }
  }, [projectId, moduleType, moduleEntityId, searchTerm, sourceFilter, filterNeedsAction]);

  useEffect(() => {
    if (moduleEntityId) {
      loadCommunications();
    }
  }, [loadCommunications, moduleEntityId]);

  const handleImportComplete = () => {
    loadCommunications();
    toast.success('Emails imported and auto-populated');
  };

  const triggerAnalysis = async (emailId, e) => {
    e.stopPropagation();
    setAnalyzingIds(prev => new Set([...prev, emailId]));
    try {
      await api.post(`/correspondence/emails/${emailId}/analyze`);
      toast.success('AI analysis complete');
      await loadCommunications();
    } catch (error) {
      toast.error('Failed to analyze email');
    } finally {
      setAnalyzingIds(prev => {
        const next = new Set(prev);
        next.delete(emailId);
        return next;
      });
    }
  };

  const handleAnalyzeThread = async () => {
    if (!moduleType || !moduleEntityId) return;
    setAnalyzingThread(true);
    setThreadAnalysis(null);
    try {
      const res = await api.correspondence.analyzeModuleCorrespondence(moduleType, moduleEntityId);
      setThreadAnalysis(res.data);
      toast.success('AI thread analysis complete');
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzingThread(false);
    }
  };

  const toggleKeyEvidence = async (item) => {
    try {
      if (item._source === 'manual') {
        await api.correspondence.update(item.id, { is_key_evidence: !item.is_key_evidence });
      } else {
        await api.patch(`/correspondence/emails/${item.id}`, { is_key_evidence: !item.is_key_evidence });
      }
      toast.success(item.is_key_evidence ? 'Removed from key evidence' : 'Marked as key evidence');
      loadCommunications();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    const ext = (file.name || file.file_name || '').split('.').pop()?.toLowerCase() || '';
    if (['pdf', 'doc', 'docx'].includes(ext)) return <FileText className="h-4 w-4 text-red-500" />;
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return <Image className="h-4 w-4 text-blue-500" />;
    return <File className="h-4 w-4 text-slate-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleAddManualEntry = async () => {
    if (!newEntry.from_email || !newEntry.subject || !newEntry.body_plain) {
      toast.error('Please fill in required fields (From Email, Subject, Body)');
      return;
    }

    setUploading(true);
    try {
      const data = {
        ...newEntry,
        to_emails: newEntry.to_emails.split(',').map(e => e.trim()).filter(Boolean)
      };
      const response = await api.correspondence.add(moduleEntityId, data);
      const correspondenceId = response.data.id;

      // Upload attachments if any
      if (pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          try {
            await api.correspondence.uploadAttachment(correspondenceId, file);
          } catch (err) {
            console.error(`Failed to upload ${file.name}:`, err);
          }
        }
      }

      toast.success(`Entry added${pendingFiles.length > 0 ? ` with ${pendingFiles.length} attachment(s)` : ''}`);
      setAddDialogOpen(false);
      setNewEntry({
        from_email: '',
        from_name: '',
        to_emails: '',
        subject: '',
        body_plain: '',
        sent_at: new Date().toISOString().split('T')[0],
        source: 'manual',
        tags: []
      });
      setPendingFiles([]);
      loadCommunications();
    } catch (error) {
      toast.error('Failed to add entry');
    } finally {
      setUploading(false);
    }
  };

  const getImpactLevel = (item) => {
    const score = item.ai_impact?.delay_risk_score || 0;
    if (score >= 70) return 'high';
    if (score >= 40) return 'medium';
    if (score > 0) return 'low';
    return 'none';
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4" data-testid="unified-communication-log">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-barlow font-bold uppercase tracking-tight text-slate-900 flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {title}
          </h3>
          <p className="text-sm text-slate-500">
            {stats.total} communications • {stats.needs_action} need action
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadCommunications} data-testid="refresh-communications-btn">
            <RefreshCw className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button 
            size="sm" 
            onClick={() => setAddDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-manual-entry-btn"
          >
            <Plus className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Add Entry</span>
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-3">
            <p className="text-xs text-slate-500 uppercase font-bold">Total</p>
            <p className="text-xl font-bold text-slate-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3">
            <p className="text-xs text-blue-600 uppercase font-bold">Emails</p>
            <p className="text-xl font-bold text-blue-700">{stats.emails}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3">
            <p className="text-xs text-purple-600 uppercase font-bold">Manual</p>
            <p className="text-xl font-bold text-purple-700">{stats.manual}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3">
            <p className="text-xs text-red-600 uppercase font-bold">High Risk</p>
            <p className="text-xl font-bold text-red-700">{stats.high_risk}</p>
          </CardContent>
        </Card>
      </div>

      {/* Email Import Drop Zone */}
      <EmailDropZone
        projectId={projectId}
        moduleType={moduleType}
        moduleEntityId={moduleEntityId}
        onImportComplete={handleImportComplete}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search communications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-communications"
          />
        </div>
        
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <Mail className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="email">Imported Emails</SelectItem>
            <SelectItem value="manual">Manual Entries</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterNeedsAction} onValueChange={setFilterNeedsAction}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <AlertTriangle className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Needs Action</SelectItem>
            <SelectItem value="no">No Action</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* AI Thread Analysis Button & Result */}
      {moduleType && moduleEntityId && communications.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">{communications.length} communication{communications.length !== 1 ? 's' : ''}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAnalyzeThread}
              disabled={analyzingThread}
              data-testid="ai-analyze-thread-btn"
              className="text-xs gap-1.5"
            >
              {analyzingThread ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyzing Thread...</>
              ) : (
                <><Brain className="h-3.5 w-3.5" /> AI Analyze Thread</>
              )}
            </Button>
          </div>

          {threadAnalysis && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-4" data-testid="ai-thread-analysis-result">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Brain className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-semibold text-indigo-700">AI Correspondence Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  {threadAnalysis.summary?.total_messages && (
                    <span className="text-xs text-indigo-500">{threadAnalysis.summary.total_messages} messages analyzed</span>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setThreadAnalysis(null)} className="h-6 w-6 p-0">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
                {threadAnalysis.analysis}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Communication List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            Loading communications...
          </div>
        ) : communications.length === 0 ? (
          <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-sm border border-slate-200">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No communications yet</p>
            <p className="text-sm mb-4">Drag & drop email files above or add entries manually</p>
            <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Entry
            </Button>
          </div>
        ) : (
          communications.map((item) => (
            <Card 
              key={`${item._source}-${item.id}`}
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                item.needs_action ? 'border-l-4 border-l-amber-500' : ''
              }`}
              onClick={() => setSelectedEmail(item)}
              data-testid={`communication-card-${item.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Impact/Source indicator */}
                  <div className={`
                    w-2 h-full min-h-[60px] rounded-full flex-shrink-0
                    ${item._source === 'email' 
                      ? (getImpactLevel(item) === 'high' ? 'bg-red-500' :
                         getImpactLevel(item) === 'medium' ? 'bg-amber-500' :
                         getImpactLevel(item) === 'low' ? 'bg-green-500' : 'bg-blue-400')
                      : 'bg-purple-400'}
                  `} />
                  
                  <div className="flex-1 min-w-0">
                    {/* Header row */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-slate-900 line-clamp-1">{item.subject || 'No Subject'}</h4>
                        <p className="text-sm text-slate-500">
                          From: {item.from_name || item.from_email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400 flex-shrink-0">
                        <Calendar className="h-3 w-3" />
                        {formatDate(item.sent_at || item.created_at)}
                      </div>
                    </div>

                    {/* AI Summary (for emails) */}
                    {item._source === 'email' && item.ai_summary && (
                      <p className="text-sm text-slate-600 mb-2 line-clamp-2 bg-slate-50 p-2 rounded">
                        <Brain className="h-3 w-3 inline mr-1 text-indigo-500" />
                        {item.ai_summary}
                      </p>
                    )}

                    {/* Body preview (for manual) */}
                    {item._source === 'manual' && item.body_plain && (
                      <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                        {item.body_plain.substring(0, 200)}
                        {item.body_plain.length > 200 && '...'}
                      </p>
                    )}

                    {/* Tags and badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Source badge */}
                      <Badge variant="outline" className={`text-xs ${SOURCE_COLORS[item._source]}`}>
                        {item._source === 'email' ? 'Email' : 'Manual'}
                      </Badge>

                      {item.has_attachments && (
                        <Badge variant="outline" className="text-xs">
                          <Paperclip className="h-3 w-3 mr-1" />
                          {item.attachments?.length || 0} files
                        </Badge>
                      )}
                      
                      {item.needs_action && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Action Required
                        </Badge>
                      )}
                      
                      {item._source === 'email' && item.ai_impact?.delay_risk_score > 0 && (
                        <Badge variant="outline" className={`text-xs ${IMPACT_COLORS[getImpactLevel(item)]}`}>
                          Risk: {item.ai_impact.delay_risk_score}%
                        </Badge>
                      )}

                      {item.is_key_evidence && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          <Star className="h-3 w-3 mr-1 fill-amber-500" />
                          Key Evidence
                        </Badge>
                      )}
                      
                      {item._source === 'email' && !item.ai_analyzed && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs h-6 px-2"
                          onClick={(e) => triggerAnalysis(item.id, e)}
                          disabled={analyzingIds.has(item.id)}
                        >
                          {analyzingIds.has(item.id) ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Brain className="h-3 w-3 mr-1" />
                              Analyze
                            </>
                          )}
                        </Button>
                      )}

                      {/* Toggle key evidence */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs h-6 px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleKeyEvidence(item);
                        }}
                      >
                        {item.is_key_evidence ? (
                          <StarOff className="h-3 w-3 text-amber-500" />
                        ) : (
                          <Star className="h-3 w-3 text-slate-400" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Email Detail Modal */}
      {selectedEmail && (
        <EmailDetailModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onUpdate={() => {
            loadCommunications();
            setSelectedEmail(null);
          }}
          projectId={projectId}
        />
      )}

      {/* Add Manual Entry Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-barlow font-bold uppercase">
              Add Communication Entry
            </DialogTitle>
            <DialogDescription>
              Manually log correspondence related to this delay event.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  From Email *
                </label>
                <Input
                  value={newEntry.from_email}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, from_email: e.target.value }))}
                  placeholder="sender@example.com"
                  data-testid="from-email-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  From Name
                </label>
                <Input
                  value={newEntry.from_name}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, from_name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                To (comma-separated)
              </label>
              <Input
                value={newEntry.to_emails}
                onChange={(e) => setNewEntry(prev => ({ ...prev, to_emails: e.target.value }))}
                placeholder="recipient1@example.com, recipient2@example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  Subject *
                </label>
                <Input
                  value={newEntry.subject}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="RE: Delay Event"
                  data-testid="subject-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  Date
                </label>
                <Input
                  type="date"
                  value={newEntry.sent_at}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, sent_at: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                Content *
              </label>
              <Textarea
                value={newEntry.body_plain}
                onChange={(e) => setNewEntry(prev => ({ ...prev, body_plain: e.target.value }))}
                placeholder="Paste the correspondence content here..."
                rows={5}
                data-testid="body-input"
              />
            </div>
            
            {/* File Attachments */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Attachments
              </label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.msg,.eml"
              />
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 rounded-sm p-4 text-center cursor-pointer hover:border-slate-400 transition-colors"
              >
                <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Click to upload files</p>
                <p className="text-xs text-slate-400">PDF, Word, Excel, Images, MSG, EML</p>
              </div>
              
              {pendingFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {pendingFiles.map((file, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm p-2"
                    >
                      <div className="flex items-center gap-2">
                        {getFileIcon(file)}
                        <span className="text-sm text-slate-700 truncate max-w-[250px]">
                          {file.name}
                        </span>
                        <span className="text-xs text-slate-400">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                      <button onClick={() => removePendingFile(idx)} className="p-1 hover:bg-slate-200 rounded">
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setAddDialogOpen(false);
              setPendingFiles([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddManualEntry}
              disabled={uploading}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-entry-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Entry
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedCommunicationLog;
